import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaUser,
  FaLock,
  FaChevronRight,
  FaHeartbeat,
  FaShieldAlt,
  FaCheckCircle,
  FaMobileAlt,
} from "react-icons/fa";

import { useNavigate, Link } from "react-router-dom";
import Navbar from "./Navbar";
import { BASE_URL } from "../backend/backend.js";

const Login = () => {
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= 768
  );

  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  const [error, setError] = useState("");

  const [isLoading, setIsLoading] =
    useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener(
      "resize",
      handleResize
    );

    return () =>
      window.removeEventListener(
        "resize",
        handleResize
      );
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]:
        e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");

    setIsLoading(true);

    try {
      const response = await fetch(
        `${BASE_URL}/api/login`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type":
              "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            username:
              formData.username,
            password:
              formData.password,
          }),
        }
      );

      if (response.ok) {
        const data =
          await response.json();

        localStorage.setItem(
          "user",
          JSON.stringify(data)
        );

        const rolePaths = {
          ADMIN: "/ad",
          DOCTOR: "/",
          USER: "/",
        };

        navigate(
          rolePaths[data.role] ||
            "/"
        );
      } else {
        setError(
          "Invalid credentials. Please try again."
        );
      }
    } catch (err) {
      console.error(err);

      setError(
        "Connection failed. Check your server."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.pageWrapper}>
      <Navbar />

      {/* Animated Background */}

      <motion.div
        animate={{
          x: [0, 40, 0],
          y: [0, -20, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
        }}
        style={styles.blob1}
      />

      <motion.div
        animate={{
          x: [0, -40, 0],
          y: [0, 30, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
        }}
        style={styles.blob2}
      />

      <motion.div
        initial={{
          opacity: 0,
          y: 50,
        }}
        animate={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          duration: 0.8,
        }}
        style={{
          ...styles.mainContainer,
          flexDirection: isMobile
            ? "column"
            : "row",
        }}
      >
        {/* LEFT SECTION */}

        <div
          style={{
            ...styles.leftSection,
            width: isMobile
              ? "100%"
              : "45%",
          }}
        >
          <motion.div
            animate={{
              rotate: [
                0, 10, -10, 0,
              ],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
            }}
            style={styles.logoBox}
          >
            GM
          </motion.div>

          <motion.h1
            initial={{
              opacity: 0,
              x: -30,
            }}
            animate={{
              opacity: 1,
              x: 0,
            }}
            transition={{
              delay: 0.2,
            }}
            style={styles.brandTitle}
          >
            Welcome To
            <br />
            <span
              style={{
                color: "#60a5fa",
              }}
            >
              Girraj Medicare
            </span>
          </motion.h1>

          <p style={styles.brandSubtitle}>
            Smart healthcare
            platform with secure
            login, digital
            prescriptions, doctor
            consultation and
            medicine management.
          </p>

          <div style={styles.featureBox}>
            <div style={styles.featureItem}>
              <FaHeartbeat />
              Healthcare Management
            </div>

            <div style={styles.featureItem}>
              <FaShieldAlt />
              Secure Authentication
            </div>

            <div style={styles.featureItem}>
              <FaMobileAlt />
              Mobile Friendly UI
            </div>

            <div style={styles.featureItem}>
              <FaCheckCircle />
              Fast Access
            </div>
          </div>
        </div>

        {/* RIGHT SECTION */}

        <div
          style={{
            ...styles.rightSection,
            width: isMobile
              ? "100%"
              : "55%",
          }}
        >
          <motion.div
            whileHover={{
              rotateX: 3,
              rotateY: -3,
            }}
            style={styles.formCard}
          >
            <h2 style={styles.loginTitle}>
              Login Account
            </h2>

            <p style={styles.loginSub}>
              Enter your credentials
              to continue
            </p>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{
                    opacity: 0,
                    y: -10,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  exit={{
                    opacity: 0,
                  }}
                  style={styles.errorBox}
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <form
              onSubmit={
                handleSubmit
              }
              style={styles.form}
            >
              {/* USERNAME */}

              <div
                style={
                  styles.inputGroup
                }
              >
                <label
                  style={
                    styles.label
                  }
                >
                  Username
                </label>

                <div
                  style={
                    styles.inputWrapper
                  }
                >
                  <FaUser
                    style={
                      styles.icon
                    }
                  />

                  <input
                    type="text"
                    name="username"
                    value={
                      formData.username
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="Enter Username"
                    required
                    style={
                      styles.input
                    }
                  />
                </div>
              </div>

              {/* PASSWORD */}

              <div
                style={
                  styles.inputGroup
                }
              >
                <label
                  style={
                    styles.label
                  }
                >
                  Password
                </label>

                <div
                  style={
                    styles.inputWrapper
                  }
                >
                  <FaLock
                    style={
                      styles.icon
                    }
                  />

                  <input
                    type="password"
                    name="password"
                    value={
                      formData.password
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="••••••••"
                    required
                    style={
                      styles.input
                    }
                  />
                </div>
              </div>

              {/* BUTTON */}

              <motion.button
                whileHover={{
                  scale: 1.03,
                }}
                whileTap={{
                  scale: 0.97,
                }}
                type="submit"
                disabled={
                  isLoading
                }
                style={
                  styles.loginBtn
                }
              >
                {isLoading
                  ? "Authenticating..."
                  : "Login Now"}

                {!isLoading && (
                  <FaChevronRight
                    style={{
                      marginLeft:
                        "10px",
                    }}
                  />
                )}
              </motion.button>
            </form>

            {/* FOOTER */}

            <div
              style={
                styles.footer
              }
            >
              <span>
                Don't have an
                account?
              </span>

              <Link
                to="/su"
                style={
                  styles.signupLink
                }
              >
                Create Account
              </Link>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

const styles = {
  pageWrapper: {
    minHeight: "100vh",
    background:
      "linear-gradient(135deg,#eff6ff,#f8fafc,#eef2ff)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding:
      "110px 20px 40px",
    position: "relative",
    overflow: "hidden",
    fontFamily:
      "'Poppins', sans-serif",
  },

  blob1: {
    position: "absolute",
    width: "500px",
    height: "500px",
    background:
      "radial-gradient(circle,#93c5fd,transparent)",
    top: "-120px",
    left: "-120px",
    filter: "blur(70px)",
    zIndex: 0,
  },

  blob2: {
    position: "absolute",
    width: "500px",
    height: "500px",
    background:
      "radial-gradient(circle,#c4b5fd,transparent)",
    bottom: "-150px",
    right: "-150px",
    filter: "blur(80px)",
    zIndex: 0,
  },

  mainContainer: {
    width: "100%",
    maxWidth: "1200px",
    display: "flex",
    borderRadius: "35px",
    overflow: "hidden",
    backdropFilter: "blur(20px)",
    background:
      "rgba(255,255,255,0.7)",
    boxShadow:
      "0 25px 50px rgba(0,0,0,0.12)",
    border:
      "1px solid rgba(255,255,255,0.3)",
    zIndex: 1,
  },

  leftSection: {
    background:
      "linear-gradient(135deg,#0f172a,#1e3a8a,#312e81)",
    color: "white",
    padding: "60px 40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
  },

  logoBox: {
    width: "75px",
    height: "75px",
    borderRadius: "22px",
    background:
      "linear-gradient(135deg,#3b82f6,#8b5cf6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: "900",
    fontSize: "1.8rem",
    marginBottom: "30px",
    boxShadow:
      "0 15px 35px rgba(59,130,246,0.5)",
  },

  brandTitle: {
    fontSize: "2.8rem",
    fontWeight: "800",
    lineHeight: 1.2,
    marginBottom: "20px",
  },

  brandSubtitle: {
    fontSize: "1rem",
    opacity: 0.85,
    lineHeight: 1.8,
    marginBottom: "35px",
  },

  featureBox: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },

  featureItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    background:
      "rgba(255,255,255,0.08)",
    padding: "14px",
    borderRadius: "14px",
    backdropFilter: "blur(8px)",
    fontWeight: "600",
  },

  rightSection: {
    padding: "50px 35px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background:
      "rgba(255,255,255,0.5)",
  },

  formCard: {
    width: "100%",
    maxWidth: "500px",
    background:
      "rgba(255,255,255,0.8)",
    borderRadius: "28px",
    padding: "40px",
    backdropFilter: "blur(20px)",
    border:
      "1px solid rgba(255,255,255,0.4)",
    boxShadow:
      "0 20px 40px rgba(0,0,0,0.08)",
    transformStyle:
      "preserve-3d",
    transition: "0.4s",
  },

  loginTitle: {
    fontSize: "2rem",
    fontWeight: "800",
    color: "#0f172a",
    marginBottom: "10px",
  },

  loginSub: {
    color: "#64748b",
    marginBottom: "30px",
  },

  errorBox: {
    background: "#fef2f2",
    color: "#dc2626",
    padding: "14px",
    borderRadius: "14px",
    marginBottom: "20px",
    fontWeight: "600",
    border:
      "1px solid #fecaca",
  },

  form: {
    display: "flex",
    flexDirection: "column",
    gap: "22px",
  },

  inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },

  label: {
    fontWeight: "700",
    color: "#334155",
    fontSize: "0.9rem",
  },

  inputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },

  icon: {
    position: "absolute",
    left: "16px",
    color: "#64748b",
  },

  input: {
    width: "100%",
    padding:
      "15px 15px 15px 48px",
    borderRadius: "16px",
    border:
      "1px solid #dbeafe",
    outline: "none",
    fontSize: "1rem",
    background:
      "rgba(255,255,255,0.9)",
    transition: "0.3s",
    boxSizing: "border-box",
  },

  loginBtn: {
    marginTop: "10px",
    border: "none",
    borderRadius: "18px",
    padding: "16px",
    background:
      "linear-gradient(135deg,#2563eb,#7c3aed)",
    color: "white",
    fontWeight: "800",
    fontSize: "1rem",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow:
      "0 15px 30px rgba(37,99,235,0.35)",
  },

  footer: {
    marginTop: "28px",
    textAlign: "center",
    color: "#64748b",
    display: "flex",
    justifyContent: "center",
    gap: "6px",
    flexWrap: "wrap",
  },

  signupLink: {
    color: "#2563eb",
    fontWeight: "700",
    textDecoration: "none",
  },
};

export default Login;